import { FormulaOperator, LogicExpressionCondition, LogicExpressionType } from '@doss/prisma/types'
import dayjs from 'dayjs'
import _ from 'lodash'
import { z } from 'zod'
import type {
  FormulaConditionInput,
  FormulaInput,
  FormulaValueInput,
  LogicExpressionInput,
  LogicExpressionValueInput,
} from '../antlr/utils/formula.js'
import { ActionData, ActionDataArray, ActionDataPrimitive } from './types/action-data.js'
import {
  ArrayLeftArrayRightCondition,
  ArrayLeftScalarRightCondition,
  Condition,
  LogicComparisonExpression,
  LogicExpression,
  LogicType,
  LogicUnaryExpression,
  ScalarLeftArrayRightCondition,
  ScalarLeftScalarRightCondition,
  isArrayLeftArrayRightCondition,
  isArrayLeftScalarRightCondition,
  isScalarLeftArrayRightCondition,
  isScalarLeftScalarRightCondition,
} from './types/logic.js'

// Define Zod schemas using z.ZodType<T> annotations to handle recursive references
const logicExpressionValueInputSchemaImpl: z.ZodType<LogicExpressionValueInput> = z
  .object({
    order: z.number().int(),

    // Exactly one of these must be provided:
    value: z.record(z.string(), z.unknown()).optional(),
    columnId: z.string().optional(),
    nestedExpression: z.lazy(() => logicExpressionInputSchemaImpl).optional(),
    formula: z.lazy(() => formulaInputSchemaImpl).optional(),
    formFieldId: z.string().optional(),
    formInputId: z.string().optional(),
  })
  .superRefine((obj, ctx) => {
    const keys = [
      'value',
      'columnId',
      'nestedExpression',
      'formula',
      'formFieldId',
      'formInputId',
    ] as const
    const setCount = keys.reduce((n, k) => n + (obj[k] === undefined || obj[k] === null ? 0 : 1), 0)
    if (setCount !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Exactly one of value, columnId, nestedExpression, formula, formFieldId, or formInputId must be set.',
      })
    }
  })

const logicExpressionInputSchemaImpl: z.ZodType<LogicExpressionInput> = z.object({
  type: z.nativeEnum(LogicExpressionType),
  condition: z.nativeEnum(LogicExpressionCondition).optional(),
  values: z.array(logicExpressionValueInputSchemaImpl),
  logicExpressionString: z.string().optional(),
})

const formulaConditionInputSchemaImpl: z.ZodType<FormulaConditionInput> = z.object({
  condition: logicExpressionInputSchemaImpl,
  order: z.number().int(),
  formula: z.lazy(() => formulaInputSchemaImpl),
})

/**
 * "Field is populated" for FormulaValueInput cross-field checks: undefined and null both mean
 * absent (omitted keys vs JSON round trips), and an empty array doesn't count
 */
const isFormulaValueFieldSet = (val: unknown): boolean => {
  if (val === undefined || val === null) {
    return false
  }
  if (Array.isArray(val) && val.length === 0) {
    return false
  }
  return true
}

const formulaValueInputSchemaImpl: z.ZodType<FormulaValueInput> = z
  .object({
    order: z.number().int(),
    // Exactly one of these must be provided. The scalar trio value/valueNumber/valueString counts
    // as a single slot: numeric literals dual-write `value` (legacy) and `valueNumber`, while
    // string literals use `valueString` only.
    tableColumnId: z.string().optional(),
    formInputId: z.string().optional(),
    formula: z.lazy(() => formulaInputSchemaImpl).optional(),
    conditions: z.array(formulaConditionInputSchemaImpl).optional(),
    value: z.number().optional(),
    valueNumber: z.number().nullable().optional(),
    valueString: z.string().nullable().optional(),
  })
  .superRefine((obj, ctx) => {
    const valueSet = isFormulaValueFieldSet(obj.value)
    const numberSet = isFormulaValueFieldSet(obj.valueNumber)
    const stringSet = isFormulaValueFieldSet(obj.valueString)

    const hasScalar = valueSet || numberSet || stringSet
    const keys = ['tableColumnId', 'formInputId', 'formula', 'conditions'] as const
    const setCount = keys.reduce(
      (n, k) => n + (isFormulaValueFieldSet(obj[k]) ? 1 : 0),
      hasScalar ? 1 : 0,
    )

    if (setCount !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Exactly one of tableColumnId, formInputId, formula, conditions (non-empty), or a scalar value must be set.',
      })
    }

    // The only legal pairing inside the scalar trio is the numeric dual-write (value +
    // valueNumber); a string literal must stand alone in valueString.
    if (stringSet && (valueSet || numberSet)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'valueString cannot be combined with value or valueNumber; string literals use valueString only.',
      })
    }

    if (valueSet && numberSet && obj.value !== obj.valueNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'value and valueNumber must match while numeric literals dual-write both fields.',
      })
    }
  })

const formulaInputSchemaImpl: z.ZodType<FormulaInput> = z.object({
  operator: z.nativeEnum(FormulaOperator),
  formulaValues: z.array(formulaValueInputSchemaImpl),
  columnOperationFilter: z
    .lazy(() => logicExpressionInputSchemaImpl)
    .nullable()
    .optional(),
  parentFormulaId: z.string().optional(),
  formulaString: z.string().optional(),
})

// Export the schemas with proper typing
export const logicExpressionInputSchema: z.ZodType<LogicExpressionInput> =
  logicExpressionInputSchemaImpl
export const formulaInputSchema: z.ZodType<FormulaInput> = formulaInputSchemaImpl

/**
 * Evaluates a logic expression and returns a boolean result. Any lookups in the expression must be
 * fully resolved to be evaluated.
 *
 * Evaluation done recursively in a post-order traversal, by applying the following rules:
 *  - If the expression is a boolean or has boolean, directly return the value
 *  - If the expression is a NOT, evaluate the expression and return the negation
 *  - If the expression is an AND, evaluate all the contained expressions and return whether all of
 *    them evaluated to `true`
 *  - If the expression is an OR, evaluate all the contained expressions and return whether any of
 *    them evaluated to `true`
 *  - If the expression is a comparison, evaluate the comparison and return the result
 *  - If the expression is a unary, evaluate the expression and return the result
 */
export function evaluateLogicExpression(expression: LogicExpression): boolean {
  if (typeof expression === 'boolean') {
    return expression
  }

  switch (expression.type) {
    case LogicType.BOOLEAN:
      return expression.value
    case LogicType.NOT:
      return !evaluateLogicExpression(expression.expression)
    case LogicType.AND:
      return expression.expressions.every(evaluateLogicExpression)
    case LogicType.OR:
      return expression.expressions.some(evaluateLogicExpression)
    case LogicType.COMPARISON: {
      const left = expression.left
      const right = expression.right
      return evaluateComparison({
        ...expression,
        left,
        right,
      })
    }
    case LogicType.UNARY: {
      return evaluateUnaryLogicExpression(expression)
    }
  }
}

/** Replaces bare datastore-variable operands with their values; non-vars stay literal. */
export function resolveDatastoreVariablesInExpression(
  expression: LogicExpression,
  datastoreValues: Record<string, ActionData | null>,
): LogicExpression {
  const deref = (operand: ActionData | null): ActionData | null =>
    typeof operand === 'string' && operand in datastoreValues ? datastoreValues[operand] : operand

  switch (expression.type) {
    case LogicType.COMPARISON:
      return { ...expression, left: deref(expression.left), right: deref(expression.right) }
    case LogicType.UNARY:
      return { ...expression, value: deref(expression.value) }
    case LogicType.AND:
    case LogicType.OR:
      return {
        ...expression,
        expressions: expression.expressions.map((nested) =>
          resolveDatastoreVariablesInExpression(nested, datastoreValues),
        ),
      }
    case LogicType.NOT:
      return {
        ...expression,
        expression: resolveDatastoreVariablesInExpression(expression.expression, datastoreValues),
      }
    case LogicType.BOOLEAN:
      return expression
  }
}

/**
 * Attempts to parse a string as a date using dayjs.
 * Returns null if the string is not a valid date format.
 * Supports various formats including ISO 8601, US format (MM/DD/YYYY), and more.
 */
function tryParseDate(value: string): Date | null {
  const parsed = dayjs(value)

  if (!parsed.isValid()) {
    return null
  }

  // Validate reasonable year range to catch common errors
  const year = parsed.year()
  if (year < 1900 || year > 2100) {
    return null
  }

  return parsed.toDate()
}

/** The conditions that only make sense on strings (substring / membership) — the date branch
 * throws for every one of them, so date sniffing must never route them there. */
function isStringOnlyCondition(
  condition: ScalarLeftScalarRightCondition,
): condition is Condition.CONTAINS | Condition.DOES_NOT_CONTAIN | Condition.IN | Condition.NOT_IN {
  return (
    condition === Condition.CONTAINS ||
    condition === Condition.DOES_NOT_CONTAIN ||
    condition === Condition.IN ||
    condition === Condition.NOT_IN
  )
}

/**
 * Evaluates a comparison between two Date objects.
 * Compares using timestamps to ensure timezone-aware comparison.
 */
function evaluateDateCondition(
  left: Date,
  right: Date,
  condition: ScalarLeftScalarRightCondition,
): boolean {
  const leftTime = left.getTime()
  const rightTime = right.getTime()

  switch (condition) {
    case Condition.EQUAL:
      return leftTime === rightTime
    case Condition.NOT_EQUAL:
      return leftTime !== rightTime
    case Condition.GREATER_THAN:
      return leftTime > rightTime
    case Condition.GREATER_THAN_OR_EQUAL:
      return leftTime >= rightTime
    case Condition.LESS_THAN:
      return leftTime < rightTime
    case Condition.LESS_THAN_OR_EQUAL:
      return leftTime <= rightTime
    case Condition.CONTAINS:
    case Condition.DOES_NOT_CONTAIN:
    case Condition.IN:
    case Condition.NOT_IN:
      // These operations don't make sense for date-to-date comparisons
      throw new Error(
        `Invalid operation "${condition}" for date comparison. ` +
          `Operations like CONTAINS, IN, etc. are not valid for comparing two dates.`,
      )
  }
}

function evaluateStringCondition(
  left: string,
  right: string,
  condition: ScalarLeftScalarRightCondition,
): boolean {
  switch (condition) {
    case Condition.EQUAL:
      return left === right
    case Condition.NOT_EQUAL:
      return left !== right
    case Condition.GREATER_THAN:
      return left > right
    case Condition.GREATER_THAN_OR_EQUAL:
      return left >= right
    case Condition.LESS_THAN:
      return left < right
    case Condition.LESS_THAN_OR_EQUAL:
      return left <= right
    case Condition.CONTAINS:
      return left.includes(right)
    case Condition.DOES_NOT_CONTAIN:
      return !left.includes(right)
    case Condition.IN:
      return right.includes(left)
    case Condition.NOT_IN:
      return !right.includes(left)
  }
}

function evaluateNumberCondition(
  left: number,
  right: number,
  condition: ScalarLeftScalarRightCondition,
): boolean {
  switch (condition) {
    case Condition.EQUAL:
      return left === right
    case Condition.NOT_EQUAL:
      return left !== right
    case Condition.GREATER_THAN:
      return left > right
    case Condition.GREATER_THAN_OR_EQUAL:
      return left >= right
    case Condition.LESS_THAN:
      return left < right
    case Condition.LESS_THAN_OR_EQUAL:
      return left <= right
    case Condition.CONTAINS:
    case Condition.DOES_NOT_CONTAIN:
    case Condition.IN:
    case Condition.NOT_IN:
      throw new Error(`Number comparison with "${condition}" condition is not supported`)
  }
}

function evaluateBooleanCondition(
  left: boolean,
  right: boolean,
  condition: ScalarLeftScalarRightCondition,
): boolean {
  switch (condition) {
    case Condition.EQUAL:
      return left === right
    case Condition.NOT_EQUAL:
      return left !== right
    case Condition.CONTAINS:
    case Condition.DOES_NOT_CONTAIN:
    case Condition.IN:
    case Condition.NOT_IN:
    case Condition.GREATER_THAN:
    case Condition.GREATER_THAN_OR_EQUAL:
    case Condition.LESS_THAN:
    case Condition.LESS_THAN_OR_EQUAL:
      throw new Error(`Boolean comparison with "${condition}" condition is not supported`)
  }
}

function evaluateArrayAndScalarCondition(
  left: ActionDataArray,
  right: ActionDataPrimitive | null,
  condition: ArrayLeftScalarRightCondition,
): boolean {
  switch (condition) {
    case Condition.CONTAINS:
      return _.includes(left, right)
    case Condition.DOES_NOT_CONTAIN:
      return !_.includes(left, right)
  }
}

function evaluateScalarAndArrayCondition(
  left: ActionDataPrimitive | null,
  right: ActionDataArray,
  condition: ScalarLeftArrayRightCondition,
): boolean {
  switch (condition) {
    case Condition.IN:
      return _.includes(right, left)
    case Condition.NOT_IN:
      return !_.includes(right, left)
  }
}

function evaluateArrayCondition<T>(
  left: T[],
  right: T[],
  condition: ArrayLeftArrayRightCondition,
): boolean {
  switch (condition) {
    case Condition.EQUAL:
      return _.isEqual(left, right)
    case Condition.NOT_EQUAL:
      return !_.isEqual(left, right)
    case Condition.CONTAINS_ANY:
      // Check that at least one value is present in both the left and right
      return _.intersection(left, right).length > 0
    case Condition.CONTAINS_ALL:
      // Check that all values in the right are present in the left
      return right.every((item) => left.includes(item))
    case Condition.DOES_NOT_CONTAIN_ANY:
      // Check that no values in the right are present in the left
      return right.every((item) => !left.includes(item))
  }
}

/**
 * Type guard for checking that all elements in an array are of the same type
 */
export function isHomogeneousArray<T extends ActionDataPrimitive | null>(
  arr: unknown[],
): arr is T[] {
  if (arr.length === 0) {
    return true // An empty array is considered homogeneous
  }

  const firstElementType = typeof arr[0]
  return arr.every((element) => typeof element === firstElementType)
}

/**
 * For an array of Action Data, checks that all the Action Data types are the same, otherwise
 * throws an error
 */
export function getHomogeneousValues(values: (ActionData | undefined)[]): ActionDataArray {
  const filteredValues = values.filter((value): value is ActionData => value !== undefined)

  if (isHomogeneousArray<string>(filteredValues)) {
    return filteredValues
  } else if (isHomogeneousArray<number>(filteredValues)) {
    return filteredValues
  } else if (isHomogeneousArray<boolean>(filteredValues)) {
    return filteredValues
  }

  throw new Error('Inconsistent data types in action data array')
}

function isNumericScalarCondition(condition: ScalarLeftScalarRightCondition): boolean {
  switch (condition) {
    case Condition.EQUAL:
    case Condition.NOT_EQUAL:
    case Condition.GREATER_THAN:
    case Condition.GREATER_THAN_OR_EQUAL:
    case Condition.LESS_THAN:
    case Condition.LESS_THAN_OR_EQUAL:
      return true
    case Condition.CONTAINS:
    case Condition.DOES_NOT_CONTAIN:
    case Condition.IN:
    case Condition.NOT_IN:
      return false
  }
}

function coerceFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  if (typeof value !== 'string') {
    return null
  }

  const trimmed = value.trim()
  if (trimmed === '') {
    return null
  }

  const number = Number(trimmed)
  return Number.isFinite(number) ? number : null
}

/**
 * Evaluates a comparison expression.
 */
function evaluateComparison(expression: LogicComparisonExpression): boolean {
  // Scalar left scalar right
  if (typeof expression.left === 'number' && typeof expression.right === 'number') {
    if (isScalarLeftScalarRightCondition(expression.condition)) {
      try {
        return evaluateNumberCondition(expression.left, expression.right, expression.condition)
      } catch (e) {
        return false
      }
    }
  }
  if (typeof expression.left === 'string' && typeof expression.right === 'string') {
    if (isScalarLeftScalarRightCondition(expression.condition)) {
      // Substring/membership are STRING operations — never sniff dates for them. The date branch
      // rejects these conditions outright, and dayjs's lenient parser reads strings like "DL-1"
      // or "Q1" as years, which turned `"DL-1" CONTAINS "1"` into a throwing date comparison.
      if (isStringOnlyCondition(expression.condition)) {
        return evaluateStringCondition(expression.left, expression.right, expression.condition)
      }

      // Try to parse both strings as dates first
      const leftDate = tryParseDate(expression.left)
      const rightDate = tryParseDate(expression.right)

      // If both are valid dates, use date comparison for accurate chronological comparison
      if (leftDate && rightDate) {
        return evaluateDateCondition(leftDate, rightDate, expression.condition)
      }

      // Otherwise, fall back to string comparison
      return evaluateStringCondition(expression.left, expression.right, expression.condition)
    }
  }
  if (typeof expression.left === 'boolean' && typeof expression.right === 'boolean') {
    if (isScalarLeftScalarRightCondition(expression.condition)) {
      return evaluateBooleanCondition(expression.left, expression.right, expression.condition)
    }
  }

  if (
    isScalarLeftScalarRightCondition(expression.condition) &&
    isNumericScalarCondition(expression.condition)
  ) {
    const leftNumber = coerceFiniteNumber(expression.left)
    const rightNumber = coerceFiniteNumber(expression.right)

    if (leftNumber !== null && rightNumber !== null) {
      return evaluateNumberCondition(leftNumber, rightNumber, expression.condition)
    }
  }

  // Scalar left array right
  if (!Array.isArray(expression.left) && Array.isArray(expression.right)) {
    if (isScalarLeftArrayRightCondition(expression.condition)) {
      return evaluateScalarAndArrayCondition(
        expression.left,
        expression.right,
        expression.condition,
      )
    }
  }

  // Array left scalar right
  if (Array.isArray(expression.left) && !Array.isArray(expression.right)) {
    if (isArrayLeftScalarRightCondition(expression.condition)) {
      return evaluateArrayAndScalarCondition(
        expression.left,
        expression.right,
        expression.condition,
      )
    }
  }

  // Array left array right
  if (Array.isArray(expression.left) && Array.isArray(expression.right)) {
    if (isArrayLeftArrayRightCondition(expression.condition)) {
      if (
        isHomogeneousArray<string>(expression.left) &&
        isHomogeneousArray<string>(expression.right)
      ) {
        return evaluateArrayCondition(expression.left, expression.right, expression.condition)
      } else if (
        isHomogeneousArray<number>(expression.left) &&
        isHomogeneousArray<number>(expression.right)
      ) {
        return evaluateArrayCondition(expression.left, expression.right, expression.condition)
      } else if (
        isHomogeneousArray<boolean>(expression.left) &&
        isHomogeneousArray<boolean>(expression.right)
      ) {
        return evaluateArrayCondition(expression.left, expression.right, expression.condition)
      } else {
        throw new Error('Array types do not match')
      }
    }
  }

  // Both left and right are null
  if (expression.left === null && expression.right === null) {
    return expression.condition === Condition.EQUAL
  }

  // Either left or right is null and the other is scalar (all the array cases are handled above)
  if (expression.left === null || expression.right === null) {
    return expression.condition === Condition.NOT_EQUAL
  }

  // Both left and right are undefined
  if (expression.left === undefined && expression.right === undefined) {
    return expression.condition === Condition.EQUAL
  }

  // Either left or right is undefined and the other is scalar
  if (expression.left === undefined || expression.right === undefined) {
    return expression.condition === Condition.NOT_EQUAL
  }

  // Catch all attempt on scalar comparisons in case of mismatched data types (i.e. "123" = 123)
  if (isScalarLeftScalarRightCondition(expression.condition)) {
    const stringLeft = expression.left.toString()
    const stringRight = expression.right.toString()

    return evaluateStringCondition(stringLeft, stringRight, expression.condition)
  }

  // This is only reachable if there is a mismatch of condition and left/right data types
  throw new Error(`Cannot evaluate comparison: ${JSON.stringify(expression)}`)
}

/**
 * Check if a value is considered empty
 */
export function isValueEmpty(value: ActionData | null | undefined): boolean {
  // The value can be undefined if, for example, the queried rows are empty and we try to access the row id of the first row
  if (value === null || value === undefined) {
    return true
  }
  // For array or string values, check that the length of the array is 0
  if (Array.isArray(value) || typeof value === 'string') {
    return value.length === 0
  }
  return false
}

/**
 * Evaluate an unary logic expression
 */
function evaluateUnaryLogicExpression(expression: LogicUnaryExpression): boolean {
  switch (expression.condition) {
    case Condition.IS_EMPTY:
      return isValueEmpty(expression.value)
    case Condition.IS_NOT_EMPTY:
      return !isValueEmpty(expression.value)
  }
}
